import * as z from "zod";

/**
 * jhl-Bots: the durable work state of a ticket. A ticket in `doing` keeps its owner bot working in
 * sessions until the bot finishes, waits or blocks it; this is the part of that state bots set.
 */

/** Most steps a plan holds; longer work is split into sub-tickets. */
export const TICKET_PLAN_MAX_STEPS = 30;
/** Longest text of one plan step. */
export const TICKET_PLAN_STEP_MAX_LENGTH = 300;
/** Longest done criterion. */
export const TICKET_DONE_CRITERIA_MAX_LENGTH = 2_000;
/** Longest result a bot reports when it finishes; long material belongs in a file it names. */
export const TICKET_RESULT_MAX_LENGTH = 8_000;
/** Longest description of what a ticket waits for. */
export const TICKET_WAIT_DETAIL_MAX_LENGTH = 500;
/** Longest reason a bot gives when it blocks a ticket. */
export const TICKET_BLOCK_REASON_MAX_LENGTH = 2_000;
const TICKET_EXTERNAL_URL_MAX_LENGTH = 500;
/** One week: the longest a bot may set a ticket aside in one wait. */
export const TICKET_WAIT_MAX_MINUTES = 7 * 24 * 60;
/** Highest session allowance per budget round. */
export const TICKET_BUDGET_MAX_SESSIONS = 500;
/** Highest hour allowance per budget round: thirty days. */
export const TICKET_BUDGET_MAX_HOURS = 24 * 30;
/** Longest command a ticket may run in the background. */
export const TICKET_COMMAND_MAX_LENGTH = 20_000;
/** How long a background command may run before its wait ends anyway, unless the bot sets a limit. */
export const TICKET_COMMAND_DEFAULT_MINUTES = 6 * 60;

/** One step of a ticket's plan; the owner checks steps off as the work goes on. */
export const TicketPlanStepSchema = z.object({
  text: z.string().trim().min(1).max(TICKET_PLAN_STEP_MAX_LENGTH),
  done: z.boolean().default(false),
});
/** One plan step as stored and shown. */
export type TicketPlanStep = z.infer<typeof TicketPlanStepSchema>;

/** A whole plan, replaced as one list on every update. */
export const TicketPlanSchema = z.array(TicketPlanStepSchema).max(TICKET_PLAN_MAX_STEPS);

/**
 * What a ticket can wait on: a person's answer, its sub-tickets, another bot's answer, a point in
 * time, a command running in the background, free memory, or the model provider's limit (set by the
 * driver, never by a bot). While it
 * waits, no session starts; the wait ends by itself when that thing happens.
 */
export const TICKET_WAIT_KINDS = [
  "person",
  "tickets",
  "bot",
  "time",
  "command",
  "quota",
  "memory",
] as const;
/** The waits a bot may set itself with ticket_wait. */
export const TICKET_BOT_WAIT_KINDS = [
  "person",
  "tickets",
  "bot",
  "time",
  "command",
  "memory",
] as const;
/** Validates a wait kind. */
export const TicketWaitKindSchema = z.enum(TICKET_WAIT_KINDS);
/** What a ticket waits on: person, tickets or time. */
export type TicketWaitKind = z.infer<typeof TicketWaitKindSchema>;

/**
 * The work allowance a requesting bot grants: sessions and hours per round. A person's own
 * requests carry none; unblocking a ticket starts a new round with the same allowance.
 */
export const TicketBudgetSchema = z
  .object({
    sessions: z.number().int().min(1).max(TICKET_BUDGET_MAX_SESSIONS).optional(),
    hours: z.number().min(0.25).max(TICKET_BUDGET_MAX_HOURS).optional(),
  })
  .refine((value) => value.sessions !== undefined || value.hours !== undefined, {
    message: "Give sessions, hours or both",
  });
/** A budget as a requesting bot names it. */
export type TicketBudget = z.infer<typeof TicketBudgetSchema>;

/** Work fields a bot may set when it creates or updates a ticket. */
export const TicketWorkInputSchema = z.object({
  doneCriteria: z.string().trim().max(TICKET_DONE_CRITERIA_MAX_LENGTH).nullable().optional(),
  plan: TicketPlanSchema.optional(),
  budget: TicketBudgetSchema.nullable().optional(),
  externalUrl: z.string().trim().url().max(TICKET_EXTERNAL_URL_MAX_LENGTH).nullable().optional(),
});
/** Validated work fields of ticket_create and ticket_update. */
export type TicketWorkInput = z.infer<typeof TicketWorkInputSchema>;

/**
 * Arguments of ticket_wait. `until` and `minutes` bound the wait; a time wait needs one of them, a
 * command wait the command to run.
 */
export const TicketWaitInputSchema = z
  .object({
    for: z.enum(TICKET_BOT_WAIT_KINDS),
    detail: z.string().trim().min(1).max(TICKET_WAIT_DETAIL_MAX_LENGTH),
    minutes: z.number().int().min(1).max(TICKET_WAIT_MAX_MINUTES).optional(),
    until: z.string().datetime({ offset: true }).optional(),
    command: z.string().trim().min(1).max(TICKET_COMMAND_MAX_LENGTH).optional(),
  })
  .refine((value) => value.for !== "time" || value.minutes !== undefined || value.until, {
    message: "A time wait needs minutes or until",
  })
  .refine((value) => (value.for === "command") === (value.command !== undefined), {
    message: "Give command exactly when waiting for a command",
  });
/** Validated arguments of ticket_wait. */
export type TicketWaitInput = z.infer<typeof TicketWaitInputSchema>;

/** The work state as tools and screens show it. */
export const TicketWorkSchema = z.object({
  ticketId: z.string(),
  parentTicketId: z.string().nullable(),
  requesterBotId: z.string().nullable(),
  doneCriteria: z.string().nullable(),
  plan: z.array(TicketPlanStepSchema),
  result: z.string().nullable(),
  externalUrl: z.string().nullable(),
  wait: z
    .object({
      for: TicketWaitKindSchema,
      detail: z.string(),
      since: z.string(),
      until: z.string().nullable(),
    })
    .nullable(),
  budget: z
    .object({
      sessions: z.number().nullable(),
      hours: z.number().nullable(),
      sessionsUsedThisRound: z.number(),
      roundStartedAt: z.string(),
    })
    .nullable(),
  sessionsUsed: z.number(),
  /** A work session is queued or running for the ticket right now. */
  sessionActive: z.boolean(),
  sessionStartedAt: z.string().nullable(),
  blockedReason: z.string().nullable(),
  blockedAt: z.string().nullable(),
});
/** A ticket's work state as tools and screens show it. */
export type TicketWork = z.infer<typeof TicketWorkSchema>;

/**
 * jhl-Bots (Arbeitsmodell): where a bot's commitment (a ticket it owns) stands, derived in one
 * place (commitmentState in core) for every surface. `running` only while a session run of it
 * is queued to start or alive; `queued` says why it is not running yet; `needs_owner` when the
 * person has to act (blocked, an open card, a wait for them); `open` for tickets not taken on
 * (todo, review). `problem` is the self-check's finding when something is stuck, else null.
 */
export const CommitmentStateSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("running"),
    starting: z.boolean(),
    since: z.string().nullable(),
    problem: z.string().nullable(),
  }),
  z.object({
    state: z.literal("queued"),
    reason: z.enum(["next", "busy", "retry"]),
    since: z.string().nullable(),
    /** When the next attempt starts, for `retry`. */
    at: z.string().nullable(),
    /** The trigger of the run the bot is busy with, for `busy`. */
    busyWith: z.string().nullable(),
    problem: z.string().nullable(),
  }),
  z.object({
    state: z.literal("waiting"),
    on: TicketWaitKindSchema,
    detail: z.string(),
    since: z.string(),
    until: z.string().nullable(),
  }),
  z.object({
    state: z.literal("needs_owner"),
    reason: z.enum(["blocked", "card", "person"]),
    detail: z.string().nullable(),
    since: z.string().nullable(),
  }),
  z.object({ state: z.literal("open"), status: z.string(), since: z.string() }),
  z.object({ state: z.literal("done"), since: z.string().nullable() }),
]);
/** Where a commitment stands, as every surface shows it. */
export type CommitmentState = z.infer<typeof CommitmentStateSchema>;
